import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { BausteinWahl, Regelwerk } from '../types';
import { BAUSTEIN_KAPITEL, bausteineDesKapitels, wortlaut } from '../kataloge/bausteine';
import { TextFeld } from './bausteine';

/**
 * Die Fliesstextkapitel: welcher Baustein gilt und in welchem Wortlaut.
 *
 * ═══ WARUM ABWAEHLEN UND UMSCHREIBEN GETRENNT SIND ════════════════════════
 * Ein abgewaehlter Baustein erscheint nicht im Plan — das ist eine
 * Entscheidung ueber den UMFANG. Ein umgeschriebener Baustein erscheint mit
 * dem eigenen Wortlaut und friert damit gegen spaetere Katalogaenderungen ein
 * — das ist eine Entscheidung ueber die FORMULIERUNG. Beides in einen
 * Schalter zu legen hiesse, dass man den Text nicht anpassen kann, ohne die
 * Aussage zu verlieren.
 *
 * Pflichtbausteine lassen sich abwaehlen, aber die Vollstaendigkeitspruefung
 * fragt dann nach: es gibt Baustellen, auf denen ein Kapitel nicht gilt, und
 * es waere anmassend, das auszuschliessen — nur unbemerkt bleiben soll es
 * nicht.
 */

interface Props {
    wahl: BausteinWahl[];
    regelwerk: Regelwerk;
    gesperrt: boolean;
    setzen: (kapitel_key: string, baustein_key: string,
        u: { aktiv?: boolean; text_override?: string }) => void;
}

export const BausteineEditor: React.FC<Props> = ({ wahl, regelwerk, gesperrt, setzen }) => {
    const [offen, setOffen] = useState<Set<string>>(new Set([BAUSTEIN_KAPITEL[0]?.key]));
    const umschalten = (k: string) => setOffen(s => {
        const n = new Set(s);
        if (n.has(k)) n.delete(k); else n.add(k);
        return n;
    });

    return (
        <ul className="space-y-2">
            {BAUSTEIN_KAPITEL.map(kap => {
                const bausteine = bausteineDesKapitels(kap.key)
                    .filter(b => !b.veraltet && (!b.regelwerk || b.regelwerk === regelwerk));
                if (bausteine.length === 0) return null;

                const aktiv = bausteine.filter(b => {
                    const w = wahl.find(x => x.kapitel_key === kap.key && x.baustein_key === b.key);
                    return w ? w.aktiv : true;
                }).length;
                const auf = offen.has(kap.key);

                return (
                    <li key={kap.key} className="border border-slate-200 rounded-xl">
                        <button
                            type="button"
                            onClick={() => umschalten(kap.key)}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
                        >
                            {auf ? <ChevronDown className="w-4 h-4 text-slate-400" />
                                : <ChevronRight className="w-4 h-4 text-slate-400" />}
                            <span className="font-bold text-slate-800 text-sm">
                                {kap.nr} · {kap.titel}
                            </span>
                            <span className="ml-auto text-xs text-slate-500">
                                {aktiv} von {bausteine.length} aktiv
                            </span>
                        </button>

                        {auf && (
                            <div className="px-3 pb-3 space-y-2">
                                {kap.hinweis && (
                                    <p className="text-[11px] text-slate-500">{kap.hinweis}</p>
                                )}
                                {bausteine.map(b => {
                                    const w = wahl.find(
                                        x => x.kapitel_key === kap.key && x.baustein_key === b.key);
                                    const istAktiv = w ? w.aktiv : true;
                                    const eigener = w?.text_override ?? '';

                                    return (
                                        <div
                                            key={b.key}
                                            className={`rounded-lg border p-2.5 ${
                                                istAktiv ? 'border-slate-200' : 'border-slate-200 bg-slate-50 opacity-60'
                                            }`}
                                        >
                                            <label className="flex items-start gap-2">
                                                <input
                                                    type="checkbox"
                                                    className="mt-1"
                                                    checked={istAktiv}
                                                    disabled={gesperrt}
                                                    onChange={e => setzen(kap.key, b.key, { aktiv: e.target.checked })}
                                                />
                                                <span className="min-w-0">
                                                    <span className="block text-sm font-bold text-slate-800">
                                                        {b.titel}
                                                        {b.pflicht && (
                                                            <span className="ml-1.5 text-[10px] font-black uppercase
                                                                             tracking-wider text-amber-700">
                                                                Pflicht
                                                            </span>
                                                        )}
                                                    </span>
                                                    <span className="block text-xs text-slate-600 mt-0.5 whitespace-pre-wrap">
                                                        {wortlaut(b, eigener)}
                                                    </span>
                                                </span>
                                            </label>

                                            {istAktiv && !gesperrt && (
                                                <div className="mt-2">
                                                    <TextFeld
                                                        label={eigener ? 'Eigener Wortlaut' : 'Wortlaut ändern'}
                                                        wert={eigener}
                                                        mehrzeilig
                                                        breit
                                                        platzhalter="leer lassen = Katalogtext gilt"
                                                        hinweis={eigener
                                                            ? 'Dieser Wortlaut gilt und ändert sich nicht mehr mit dem Katalog.'
                                                            : undefined}
                                                        onSpeichern={v => setzen(kap.key, b.key, { text_override: v })}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};
